import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';

const MIN_FRAME_HEIGHT = 240;
const MAX_FRAME_HEIGHT = 6000;
const FORBIDDEN_TAGS = [
  'script', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'form', 'input', 'button', 'textarea', 'select', 'option', 'base', 'meta', 'link',
];

export const sanitizeMailHtml = (html = '') => {
  const sanitized = DOMPurify.sanitize(String(html), {
    USE_PROFILES: { html: true },
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: ['srcdoc'],
    ADD_DATA_URI_TAGS: ['img'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });

  if (!sanitized || typeof DOMParser === 'undefined') return sanitized;
  const document = new DOMParser().parseFromString(`<body>${sanitized}</body>`, 'text/html');
  document.querySelectorAll('a[href]').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
  document.querySelectorAll('img').forEach((image) => {
    image.setAttribute('loading', 'lazy');
    image.setAttribute('referrerpolicy', 'no-referrer');
  });
  return document.body.innerHTML;
};

export const buildMailHtmlDocument = (html = '') => `<!doctype html>
<html lang="ko">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: http: data:; style-src 'unsafe-inline'; font-src https: http: data:; media-src https: http: data:; script-src 'none'; frame-src 'none'; object-src 'none'; connect-src 'none'; form-action 'none'; base-uri 'none'">
    <style>
      html { color-scheme: light; background: #fff; }
      body { margin: 0; min-width: 0; overflow-wrap: anywhere; color: #1f2937; background: #fff; font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.65; }
      img, video { max-width: 100% !important; height: auto !important; }
      table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
      td, th { max-width: 100% !important; overflow-wrap: anywhere; }
      pre { white-space: pre-wrap; overflow-wrap: anywhere; }
    </style>
  </head>
  <body>${sanitizeMailHtml(html)}</body>
</html>`;

const MailHtmlBody = ({ html, title = 'HTML 메일 본문' }) => {
  const observerRef = useRef(null);
  const [height, setHeight] = useState(MIN_FRAME_HEIGHT);
  const sourceDocument = useMemo(() => buildMailHtmlDocument(html), [html]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const resizeFrame = (event) => {
    observerRef.current?.disconnect();
    const frameDocument = event.currentTarget.contentDocument;
    if (!frameDocument) return;
    const resize = () => {
      const contentHeight = Math.max(
        frameDocument.body?.scrollHeight || 0,
        frameDocument.documentElement?.scrollHeight || 0,
        MIN_FRAME_HEIGHT,
      );
      setHeight(Math.min(contentHeight, MAX_FRAME_HEIGHT));
    };
    resize();
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(resize);
      observerRef.current.observe(frameDocument.documentElement);
    }
  };

  return (
    <FrameShell>
      <HtmlFrame
        title={title}
        srcDoc={sourceDocument}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        $height={height}
        onLoad={resizeFrame}
      />
    </FrameShell>
  );
};

export default MailHtmlBody;

const FrameShell = styled.div`
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const HtmlFrame = styled.iframe`
  display: block;
  width: 100%;
  height: ${({ $height }) => $height}px;
  border: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
`;
